import { useQuery, type Query } from '@tanstack/react-query'
import { fetchAPPost } from '@/services/postsService'
import type { Post } from '@/types/posts'

/** Fresh-post staleTime; a resolved post rarely changes once fetched. */
export const AP_POST_STALE_MS = 5 * 60 * 1000
/** Short staleTime for a null (not-found) resolve, so it recovers instead of sticking for 5 minutes. */
export const AP_POST_NULL_STALE_MS = 15_000

/**
 * Options for the `apPost` query, extracted so the staleTime logic can be
 * unit-tested directly. Mobile also gets bounded retries and a query-scoped
 * refetch schedule for null resolves; webapp keeps React Query's defaults for
 * retry/refetch and only fixes the sticky-null staleTime here.
 */
export function apPostQueryOptions(uri: string | undefined) {
    return {
        queryKey: ['apPost', uri],
        queryFn: () => fetchAPPost(uri!),
        enabled: !!uri,
        staleTime: (query: Query<Post | null, Error>) =>
            query.state.data === null ? AP_POST_NULL_STALE_MS : AP_POST_STALE_MS,
        gcTime: 10 * 60 * 1000,
    }
}

export function useAPPost(uri: string | undefined) {
    const query = useQuery(apPostQueryOptions(uri))

    return {
        post: query.data ?? null,
        isLoading: query.isLoading,
        error: query.error,
    }
}
